import {TimeStretchCode} from "../Gemini/GeminiTimeStretch";

export const Rate = 24000;

// Keep the resampling phase across render quanta, including non-integer rate ratios.
export function CaptureCode(): string {
    return `
    class Capture extends AudioWorkletProcessor {
        constructor() {
            super();
            this.next = 0;
            this.offset = 0;
            this.last = 0;
            this.used = 0;
            this.muted = false;
            this.chunk = new Int16Array(480);
            this.port.onmessage = e => this.muted = e.data;
        }
        process(inputs, outputs) {
            const data = inputs[0] && inputs[0][0];
            const length = data ? data.length : outputs[0][0].length;
            for (let i = 0; i < length; i++, this.offset++) {
                const current = this.muted || !data ? 0 : data[i];
                if (this.offset === 0) this.last = current;
                while (this.next <= this.offset) {
                    const weight = this.next - this.offset + 1;
                    const value = Math.max(-1, Math.min(1, this.last + (current - this.last) * weight));
                    this.chunk[this.used++] = value < 0 ? value * 32768 : value * 32767;
                    this.next += sampleRate / ${Rate};
                    if (this.used === this.chunk.length) {
                        this.port.postMessage(this.chunk.buffer, [this.chunk.buffer]);
                        this.chunk = new Int16Array(480);
                        this.used = 0;
                    }
                }
                this.last = current;
            }
            return true;
        }
    }
    registerProcessor('openai-capture', Capture);`;
}

export function PlaybackCode(): string {
    return `
    ${TimeStretchCode()}
    class Playback extends AudioWorkletProcessor {
        constructor(options) {
            super();
            this.speed = options.processorOptions.speed;
            this.native = this.speed === 1;
            this.stretch = new PitchPreservingTimeStretch(this.speed, sampleRate);
            this.silent = 0;
            this.quiet = sampleRate;
            this.speaking = false;
            this.idle = true;
            this.since = 0;
            this.padded = false;
            this.playing = false;
            this.wait = 0;
            this.packet = 0;
            this.still = sampleRate;
            this.fade = 0;
            this.ramp = Math.max(1, Math.round(sampleRate * 0.004));
            this.gap = 0;
            this.late = 0;
            this.arrival = 0;
            this.clock = 0;
            this.received = 0;
            this.marks = [];
            this.cadence = 1;
            this.adapting = false;
            this.low = 0;
            this.tempo = this.speed;
            this.port.onmessage = e => {
                const data = e.data;
                if (data.type === 'speed') {
                    this.speed = data.speed;
                    this.tempo = this.speed;
                    if (this.speed !== 1) this.native = false;
                    this.stretch.setSpeed(this.speed);
                } else if (data.type === 'reset') {
                    this.stretch = new PitchPreservingTimeStretch(this.speed, sampleRate);
                    this.native = this.speed === 1;
                    this.silent = 0;
                    this.playing = false;
                    this.padded = false;
                    this.since = 0;
                    this.wait = 0;
                    this.packet = 0;
                    this.still = sampleRate;
                    this.fade = 0;
                    this.gap = 0;
                    this.late = 0;
                    this.arrival = 0;
                    this.clock = 0;
                    this.received = 0;
                    this.marks = [];
                    this.cadence = 1;
                    this.adapting = false;
                    this.low = 0;
                    this.tempo = this.speed;
                } else if (data.type === 'chunk') {
                    this.arrival = 0;
                    let chunk = data.chunk;
                    // Measure audio seconds received per playback second, including
                    // silent keepalives. Short delivery jitter averages out here.
                    this.received += chunk.length;
                    this.marks.push({time: this.clock, samples: this.received});
                    while (this.marks.length > 2 && this.clock - this.marks[1].time >= sampleRate * 2) this.marks.shift();
                    const first = this.marks[0];
                    const elapsed = this.clock - first.time;
                    if (elapsed >= sampleRate * 0.5) this.cadence = (this.received - first.samples) / elapsed;
                    let peak = 0;
                    for (const value of chunk) peak = Math.max(peak, Math.abs(value));
                    if (peak < 0.0001) {
                        // Keep the leading silent tail even when one packet crosses
                        // the limit. A short word still needs this tail to start/drain.
                        const keep = Math.max(0, Math.floor(sampleRate * 0.25) - this.silent);
                        this.silent += chunk.length;
                        chunk = chunk.subarray(0, keep);
                    } else this.silent = 0;
                    if (chunk.length > 0) {
                        this.since = 0;
                        this.packet = Math.max(this.packet, chunk.length);
                        this.padded = false;
                        this.stretch.add(chunk);
                    }
                    const reserve = sampleRate * (0.16 * Math.max(1, this.speed) + 0.04);
                    const queued = this.stretch.pending;
                    // A sustained delivery deficit cannot be fixed by rebuffering:
                    // every restart would consume the same reserve again. Engage
                    // pitch-preserving slowdown only when that reserve is shrinking.
                    const low = this.playing && this.silent === 0 && elapsed >= sampleRate * 0.5 &&
                        this.cadence < this.speed * 0.995 && queued < this.packet + reserve * 0.6;
                    this.low = low ? this.low + 1 : 0;
                    const was = this.adapting;
                    if (this.low >= 2) this.adapting = true;
                    if (this.adapting) {
                        const target = this.packet + reserve;
                        this.tempo = Math.max(Math.min(0.8, this.speed),
                            Math.min(this.speed, this.cadence + (queued - target) / sampleRate * 0.5));
                        if (this.tempo !== 1) this.native = false;
                        this.stretch.setSpeed(this.tempo);
                        if (!was) this.port.postMessage({type: 'pace', requested: this.speed,
                            speed: this.tempo, incoming: this.cadence, queued: queued * 1000 / sampleRate});
                    }
                    // Ignored silent keepalives must not postpone the tail flush.
                    if (this.stretch.pending > sampleRate * 10) this.port.postMessage({type: 'overflow'});
                }
            };
        }
        process(inputs, outputs) {
            const samples = outputs[0][0];
            this.since += samples.length;
            this.arrival += samples.length;
            this.clock += samples.length;
            const tail = this.since >= Math.max(sampleRate * 0.35, this.packet + sampleRate * 0.2);
            // Network packet gaps are not the end of an utterance. Accumulate a
            // small reserve before playback and after an underrun.
            const buffer = sampleRate * (0.16 * Math.max(1, this.speed) + 0.04);
            if (!this.playing && this.stretch.pending > 0) {
                this.wait += samples.length;
                // A large first packet is not a time reserve: wait for 200 ms of
                // arrivals too, so a later 100-180 ms stall does not empty playback.
                if ((this.wait >= sampleRate * 0.2 && this.stretch.pending >= buffer) || tail) this.playing = true;
            }
            if (!this.padded && tail && this.stretch.pending > 0) {
                // Flush only the remaining WSOLA window, after a longer idle gap.
                // At native speed the samples pass through without any padding.
                if (!this.native) this.stretch.add(new Float32Array(Math.floor(sampleRate * 0.08)));
                this.padded = true;
            }
            samples.fill(0);
            if (this.playing) {
                const before = this.stretch.pending;
                this.stretch.process(samples, true);
                if (this.stretch.pending !== before && this.gap > 0) {
                    if (this.gap >= sampleRate * 0.05) this.port.postMessage({type: 'gap',
                        ms: this.gap * 1000 / sampleRate, arrival: this.late * 1000 / sampleRate,
                        queued: before * 1000 / sampleRate, speed: this.tempo});
                    this.gap = 0;
                    this.late = 0;
                }
                if (this.stretch.pending === before) { this.playing = false; this.wait = 0; }
                if (this.stretch.pending === 0 ||
                    (!this.native && (this.padded || this.silent >= sampleRate * 0.25) && this.stretch.pending < sampleRate * 0.05)) {
                    this.stretch = new PitchPreservingTimeStretch(this.speed, sampleRate);
                    this.native = this.speed === 1;
                    this.adapting = false;
                    this.low = 0;
                    this.tempo = this.speed;
                    this.playing = false;
                    this.wait = 0;
                }
                // Report starvation after recent audible output, not normal idle silence.
                if (!this.playing && this.quiet < sampleRate * 0.05 && this.gap === 0) this.gap = 1;
            }
            if (this.silent >= sampleRate * 0.25) { this.gap = 0; this.late = 0; }
            else if (this.gap > 0 && !this.playing) {
                this.gap += samples.length;
                this.late = Math.max(this.late, this.arrival);
            }
            let peak = 0;
            for (let i = 0; i < samples.length; i++) {
                const value = samples[i];
                if (Math.abs(value) < 0.0001) this.still++;
                else {
                    // Fade the onset after real silence, including silence inside
                    // a packet. Never restart the fade at ordinary packet boundaries.
                    if (this.still >= sampleRate * 0.02) this.fade = this.ramp;
                    this.still = 0;
                }
                if (this.fade > 0) {
                    const gain = (this.ramp - this.fade + 1) / this.ramp;
                    samples[i] *= 0.5 - 0.5 * Math.cos(Math.PI * gain);
                    this.fade--;
                }
                peak = Math.max(peak, Math.abs(samples[i]));
            }
            this.quiet = peak > 0.003 ? 0 : this.quiet + samples.length;
            const speaking = this.quiet < sampleRate * 0.35;
            if (speaking !== this.speaking) {
                this.speaking = speaking;
                this.port.postMessage({type: 'speaking', value: speaking});
            }
            const idle = !speaking && this.stretch.pending < sampleRate * 0.05;
            if (idle && !this.idle) this.port.postMessage({type: 'idle'});
            this.idle = idle;
            return true;
        }
    }
    registerProcessor('openai-playback', Playback);`;
}

async function Load(context: AudioContext, code: string): Promise<void> {
    const url = URL.createObjectURL(new Blob([code], {type: "application/javascript"}));
    try { await context.audioWorklet.addModule(url); }
    finally { URL.revokeObjectURL(url); }
}

export class Input {
    private context: AudioContext | null = null;
    private stream: MediaStream | null = null;
    private node: AudioWorkletNode | null = null;
    private closed = false;

    async Start(callback: (bytes: ArrayBuffer) => void, muted: boolean): Promise<void> {
        this.stream = await navigator.mediaDevices.getUserMedia({audio: {
            channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true
        }});
        if (this.closed) { this.Stop(); throw new Error("Capture was cancelled."); }
        // The worklet resamples the device's actual rate to 24 kHz.
        this.context = new AudioContext({latencyHint: "interactive"});
        await Load(this.context, CaptureCode());
        if (this.closed) throw new Error("Capture was cancelled.");
        this.node = new AudioWorkletNode(this.context, "openai-capture");
        this.node.port.onmessage = event => callback(event.data);
        this.Mute(muted);
        this.context.createMediaStreamSource(this.stream).connect(this.node);
        this.node.connect(this.context.destination);
        await this.context.resume();
    }

    Mute(muted: boolean): void { this.node?.port.postMessage(muted); }

    Stop(): void {
        this.closed = true;
        if (this.node) { this.node.port.onmessage = null; this.node.disconnect(); this.node = null; }
        this.stream?.getTracks().forEach(track => track.stop());
        this.stream = null;
        void this.context?.close().catch(console.error);
        this.context = null;
    }
}

export class Output {
    private context: AudioContext | null = null;
    private node: AudioWorkletNode | null = null;
    private closed = false;
    private idle = true;
    private last = 0;

    async Start(speed: number, playback: (speaking: boolean) => void, failed: () => void,
                report: (ms: number, arrival: number, queued: number, speed: number) => void,
                adjust: (requested: number, speed: number, incoming: number, queued: number) => void): Promise<void> {
        this.context = new AudioContext({sampleRate: Rate, latencyHint: "interactive"});
        await Load(this.context, PlaybackCode());
        if (this.closed) throw new Error("Playback was cancelled.");
        this.node = new AudioWorkletNode(this.context, "openai-playback", {
            numberOfInputs: 0, outputChannelCount: [1], processorOptions: {speed}
        });
        this.node.port.onmessage = event => {
            if (event.data.type === "idle") this.idle = true;
            if (event.data.type === "speaking") {
                if (event.data.value) { this.idle = false; this.last = performance.now(); }
                playback(event.data.value);
            }
            if (event.data.type === "overflow") failed();
            if (event.data.type === "gap") report(event.data.ms, event.data.arrival, event.data.queued, event.data.speed);
            if (event.data.type === "pace") adjust(event.data.requested, event.data.speed, event.data.incoming, event.data.queued);
        };
        this.node.connect(this.context.destination);
        await this.context.resume();
        const wnd = window as any;
        const mediator = wnd.audioMediator ??= {listeners: []};
        mediator.stream = {context: this.context, node: this.node};
        const listeners = typeof mediator.listeners === "function" ? [mediator.listeners] : Object.values(mediator.listeners ?? {});
        for (const listener of listeners) if (typeof listener === "function") listener(mediator.stream);
    }

    Play(bytes: ArrayBuffer): void {
        if (this.closed || !this.node) return;
        const pcm = new DataView(bytes);
        if (bytes.byteLength % 2) throw new Error("Incomplete PCM16 output sample.");
        const chunk = new Float32Array(bytes.byteLength / 2);
        let peak = 0;
        for (let i = 0; i < chunk.length; i++) {
            chunk[i] = pcm.getInt16(i * 2, true) / 32768;
            peak = Math.max(peak, Math.abs(chunk[i]));
        }
        if (peak > 0.003) { this.last = performance.now(); this.idle = false; }
        this.node.port.postMessage({type: "chunk", chunk}, [chunk.buffer]);
    }

    Speed(speed: number): void {
        this.node?.port.postMessage({type: "speed", speed: Number.isFinite(speed) && speed > 0 ? speed : 1});
    }

    Reset(): void { this.node?.port.postMessage({type: "reset"}); }

    async Drain(): Promise<void> {
        const start = performance.now();
        while (!this.closed) {
            const now = performance.now();
            if (this.idle && now - Math.max(start, this.last) >= 1500) return;
            if (now - start > 30000) throw new Error("OpenAI playback did not become idle.");
            await new Promise(resolve => setTimeout(resolve, 50));
        }
    }

    Stop(): void {
        this.closed = true;
        const mediator = (window as any).audioMediator;
        if (mediator?.stream?.node === this.node) mediator.stream = null;
        if (this.node) { this.node.port.onmessage = null; this.node.disconnect(); this.node = null; }
        void this.context?.close().catch(console.error);
        this.context = null;
    }
}
